import { type GridColumn } from "@common/controls/Grid/types";
import GridView, { type GridViewProps } from "@common/controls/GridView/component";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { formatMoneyDisplay } from "@common/lib/format";
import { isNullOrUndefined } from "@common/lib/misc";
import Network from "@common/modules/network/types";
import { getUserDisplayName } from "@common/modules/user/lib";
import * as React from "react";

type VolumeDrillDownGridProps = {
  data: Network.CommissionRules.Api.GenealogyHierarchyUserVolume[];
  onRowClick: (item: Network.CommissionRules.Api.GenealogyHierarchyUserVolume) => void;
  volumeTypeLabel: string;
};

export const VolumeDrillDownGrid = (props: VolumeDrillDownGridProps) => {
  const i18n = useI18n();

  const volumeDrillDownGridData = React.useMemo((): GridViewProps<
    Network.CommissionRules.Api.GenealogyHierarchyUserVolume,
    Network.CommissionRules.Api.GenealogyHierarchyUserVolume
  > => {
    const items: Network.CommissionRules.Api.GenealogyHierarchyUserVolume[] = Object.values(
      props.data.reduce((acc, curr) => {
        const { orderId, volume, currencyCode, quantity } = curr;
        if (!acc[orderId]) {
          acc[orderId] = { ...curr };
        } else {
          acc[orderId].volume += quantity * volume;
          acc[orderId].currencyCode ??= currencyCode;
        }
        return acc;
      }, {} as Record<string, Network.CommissionRules.Api.GenealogyHierarchyUserVolume>)
    );

    const columns: GridColumn<Network.CommissionRules.Api.GenealogyHierarchyUserVolume>[] = [
      {
        key: "orderNumber",
        name: i18n.order,
        dataType: "string",
        qa: "Order number"
      },
      {
        key: "orderPaidOnDate",
        name: i18n.paid,
        dataType: "dateTime",
        visibility: () => items.some((x) => !isNullOrUndefined(x.orderPaidOnDate)),
        qa: "Paid"
      },
      {
        key: "userFirstName",
        name: i18n.contact,
        dataType: "string",
        data: (x) => getUserDisplayName({ userFirstName: x.userFirstName, userLastName: x.userLastName }),
        visibility: () => items.some((x) => !isNullOrUndefined(x.userLastName) || !isNullOrUndefined(x.userLastName)),
        qa: "Contact"
      },
      {
        key: "absoluteLevel",
        name: i18n.absoluteLevel,
        dataType: "number",
        visibility: () =>
          items.some((x) => !isNullOrUndefined(x.absoluteLevel)) && items.some((x) => x.level !== x.absoluteLevel),
        qa: "Absolute level"
      },
      {
        key: "level",
        name: i18n.level,
        dataType: "number",
        visibility: () => items.some((x) => !isNullOrUndefined(x.level)),
        qa: "Level"
      },
      {
        key: "breakawayLevel",
        name: i18n.breakawayLevel,
        dataType: "number",
        visibility: () => items.some((x) => !isNullOrUndefined(x.breakawayLevel)),
        qa: "Breakaway level"
      },
      {
        key: "differentialLevel",
        name: i18n.differentialLevel,
        dataType: "number",
        visibility: () => items.some((x) => !isNullOrUndefined(x.differentialLevel)),
        qa: "Differential level"
      },
      {
        key: "volume",
        name: props.volumeTypeLabel,
        dataType: "string",
        data: (x) => formatMoneyDisplay(x.volume * x.quantity, x.currencyCode ?? undefined)
      }
    ];

    return {
      idKey: "orderId",
      columns: columns,
      onRowClick: props.onRowClick,
      data: sort(items, { key: "orderNumber" }),
      isLoading: false,
      toolbar: {
        showAdd: false,
        showSearch: false
      }
    };
  }, [i18n, props.data, props.volumeTypeLabel, props.onRowClick]);

  return (
    <GridView pageSize={10} {...volumeDrillDownGridData} idKey="orderNumber" isLoading={false} useSearchUrl={false} />
  );
};
